import React, { useRef, useState } from 'react';

import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Grid, Divider, Hidden, Typography } from '@material-ui/core';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

import { VALIDATORS_MAP } from 'components/FormComponents/const';
import config from 'config/Config';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { authModel, passwordModel } from 'models';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';
import { CATEGORY_NAMES, METRIC_EVENT_NAMES } from 'metrics/constants';
import { useMatomo } from 'contexts/MatomoContext';
import useIsMobile from 'hooks/useIsMobile';

import { AuthMessage } from '../AuthMessage/AuthMessage';

import { AuthCard, FormLink, SmallInfoText, StyledButtonComponent, TextValidator } from '../style';

function ResetPassword() {
  const captchaRef = useRef<HCaptcha>(null);
  const { trackEvent } = useMatomo();
  const [state, setState] = useState({
    space: '',
    email: '',
  });
  const [emailSent, setEmailSent] = useState(false);

  const { t } = useTranslation();
  const { handleServerError } = useServerErrorActions();
  const isMobile = useIsMobile();

  const handlePost = () => {
    captchaRef.current?.execute();
  };

  const onCaptchaVerify = (token: string) => {
    passwordModel
      .createPassword({ email: state.email, hCaptchaResponse: token, clientName: state.space })
      .then(() => {
        trackEvent({
          category: CATEGORY_NAMES.PUBTOOL_EVENTS,
          action: 'click',
          name: METRIC_EVENT_NAMES.RESET_PASSWORD,
        });
        setEmailSent(true);
      })
      .catch(error => handleServerError(error));
  };

  const handleChange = (item: any) =>
    setState(prevState => ({
      ...prevState,
      ...item,
    }));

  const onBackToSignIn = () => {
    trackEvent({ category: CATEGORY_NAMES.PUBTOOL_EVENTS, action: 'click', name: METRIC_EVENT_NAMES.SIGN_IN });
    redirectToSignIn();
  };

  const disableFormButton = state.email === '';

  if (emailSent) {
    return (
      <AuthMessage
        tabTitle={t('pages.auth.ResetPassword.tabTitle')}
        title={t('pages.auth.ResetPassword.emailSentTitle')}
        text={t('pages.auth.ResetPassword.emailSentText')}
        buttonText={isMobile ? undefined : t('pages.auth.ResetPassword.return')}
        onClick={onBackToSignIn}
        disclaimer=""
      />
    );
  }

  return (
    <AuthCard>
      <Helmet title={t('pages.auth.ResetPassword.tabTitle')} />
      <Grid container direction="column" spacing={6}>
        <Grid item>
          <Typography variant="h2">{t('pages.auth.ResetPassword.title')}</Typography>
        </Grid>
        <Grid item>
          <SmallInfoText>{t('pages.auth.ResetPassword.subtext')}</SmallInfoText>
        </Grid>
        <Grid item>
          <ValidatorForm onSubmit={handlePost}>
            <TextValidator
              validators={[VALIDATORS_MAP.lowercaseLettersNumbersUnderscoreDashes]}
              label={t('common.forms.labels.spaceName')}
              value={state.space}
              fieldName={authModel.fields.space}
              handleChange={handleChange}
              autoComplete="on"
            />
            <TextValidator
              validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.isEmail]}
              label={t('common.forms.labels.email')}
              value={state.email}
              fieldName={authModel.fields.email}
              type="email"
              handleChange={handleChange}
              autoComplete="email"
            />
            <StyledButtonComponent
              fullWidth
              color="primary"
              type="submit"
              text={t('pages.auth.ResetPassword.submit')}
              disabled={disableFormButton}
            />
            <HCaptcha onVerify={onCaptchaVerify} sitekey={config.captcha_key} ref={captchaRef} size="invisible" />
          </ValidatorForm>
        </Grid>
        <Hidden smDown>
          <Grid item>
            <Divider />
          </Grid>
          <Grid item style={{ textAlign: 'center' }}>
            <FormLink variant="text" text={t('pages.auth.ResetPassword.return')} onClick={onBackToSignIn} />
          </Grid>
        </Hidden>
      </Grid>
    </AuthCard>
  );
}

export default ResetPassword;
